import { useDateFormat } from '@vueuse/core';

export function formatedDate(date: string, format: string) {
  return useDateFormat(new Date(date), format);
}

/**
 * @param params: date as string
 *  accepted formats: "2022-04-02 08:46:38", "2025-03-09T10:39:58Z", "2025-03-09"
 * @returns {string} german date time string "02.04.2022 08:46:38"
 */
export function formatToGermanDateTimeString(params: any): string {
  if (!params || params?.value === null || params?.value === undefined) {
    return '';
  }
  const str: string[] = params.value.substr(0, 10).split('-');
  const time = params.value.substr(11) ? params.value.substr(11, 8) : '00:00:00';
  const year = str[0];
  const month = str[1].length === 1 ? `0${str[1]}` : str[1];
  const day = str[2];
  return `${day}.${month}.${year} ${time}`;
}

/**
 * @param params: date as string
 *  accepted format: "2025-02-18T08:47:56.969945124Z"
 * @param withTime: boolean, whether to include the time
 * @param withSeconds: boolean, whether to include seconds in the time
 * @param useComma: boolean, whether to use a comma between date and time
 * @returns {string} german date time string e. g. "18.02.2025 08:47:50"
 */
export function formatISOToGermanDate(
  isoDateStr: string,
  withTime = true,
  withSeconds = true,
  useComma = false
): string {
  const date = new Date(isoDateStr);

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');

  const dateStr = `${day}.${month}.${year}`;
  let timeStr = `${hours}:${minutes}`;

  if (withSeconds) {
    timeStr += `:${seconds}`;
  }

  const separator = useComma ? ', ' : ' ';

  return withTime ? `${dateStr}${separator}${timeStr}` : dateStr;
}

export function formatDateToGerman(date: Date | null): string {
  if (!date) return '';
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  return date.toLocaleString('de-DE', options);
}

export function formatDatesToGerman(dates: (Date | null)[]): string {
  return dates
    .map((date) => {
      if (!date) return '';
      const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      };
      return date.toLocaleString('de-DE', options);
    })
    .filter((date) => date !== '')
    .join(' - ');
}

export function formatDateRangeToGerman(dateFrom: Date | null, dateUntil: Date | null): string {
  if (!dateFrom || !dateUntil) return '';
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  const dateFromString = dateFrom.toLocaleString('de-DE', options);
  const dateUntilString = dateUntil.toLocaleString('de-DE', options);
  return `${dateFromString} - ${dateUntilString}`;
}

export function formatDateToGermanDateTimeString(date: Date | null, withTime = true, withComma = true): string {
  if (!date) return '';
  const options: Intl.DateTimeFormatOptions = withTime
    ? {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }
    : {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      };

  let formattedDate = date.toLocaleString('de-DE', options);

  if (!withComma) {
    formattedDate = formattedDate.replace(',', '');
  }

  return formattedDate;
}

export function formatDate(date: Date | string): string {
  const formattedDate = new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
  });
  return formattedDate.format(new Date(date).setHours(1, 0, 0, 0));
}
